import { endOfDay, startOfDay } from 'date-fns';
import { EnvironmentsNames, EVENT_TYPES, GetEventsQuery, SortableEventColumns } from 'utils';
import * as z from 'zod/v4';

export const filtersSchema = z.object({
  dateRange: z
    .object({
      from: z.coerce.date().optional().nullable(),
      to: z.coerce.date().optional().nullable(),
    })
    .optional(),
  enabled: z.boolean().optional(),
  qa_approved: z.boolean().optional(),
  inExperiment: z.boolean().optional(),
  noChildRelation: z.boolean().optional(),
  parentChildRelation: z.boolean().optional(),
  collectionIds: z.uuid().array().optional(),
  event_type: z.array(z.enum(EVENT_TYPES)).optional(),
  getGrandChildren: z.boolean().optional(),
  levelRange: z
    .object({
      from: z.number().nullable(),
      to: z.number().nullable(),
    })
    .optional(),
  serverRange: z
    .object({
      from: z.number().nullable(),
      to: z.number().nullable(),
    })
    .optional(),
  withoutServerTargeting: z.boolean().optional(),
});

export type FiltersSchema = z.infer<typeof filtersSchema>;

export type BuildGetEventsFilterParams = {
  range: [Date | undefined, Date | undefined];
  env: EnvironmentsNames;
  getGrandChildren?: boolean;
  filters?: FiltersSchema;
  sortBy?: SortableEventColumns;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  permanent?: boolean;
  checkBrokenRefs?: boolean;
  page?: number;
  pageSize?: number;
};

export const buildGetEventsFilter = ({
  range,
  env,
  filters = {},
  sortBy,
  sortOrder,
  search,
  permanent,
  checkBrokenRefs,
  page,
  getGrandChildren,
  pageSize,
}: BuildGetEventsFilterParams) => {
  const {
    dateRange: filterDateRange,
    levelRange: filterLevelRange,
    serverRange: filterServerRange,
    withoutServerTargeting,
    enabled,
    inExperiment,
    noChildRelation,
    parentChildRelation,
    qa_approved,
    event_type,
    collectionIds,
  } = filters;

  const dateRange =
    filterDateRange?.from || filterDateRange?.to
      ? {
          from: filterDateRange.from ? startOfDay(filterDateRange.from) : range[0],
          to: filterDateRange.to ? endOfDay(filterDateRange.to) : range[1],
        }
      : { from: range[0], to: range[1] };

  const levelRange = filterLevelRange
    ? {
        from: filterLevelRange.from ?? undefined,
        to: filterLevelRange.to ?? undefined,
      }
    : { from: undefined, to: undefined };

  const filter: GetEventsQuery = {
    range: { from: dateRange.from, to: dateRange.to },
    levelRange,
    env,
    ...(filterServerRange &&
      (filterServerRange.from !== null || filterServerRange.to !== null) && {
        serverRange: {
          from: filterServerRange.from ?? undefined,
          to: filterServerRange.to ?? undefined,
        },
      }),
    ...(withoutServerTargeting !== undefined && { withoutServerTargeting }),
    ...(getGrandChildren !== undefined && { getGrandChildren }),
    ...(noChildRelation !== undefined && { noChildRelation }),
    ...(parentChildRelation !== undefined && { parentChildRelation }),
    ...(inExperiment !== undefined && { inExperiment }),
    ...(collectionIds && collectionIds.length > 0 && { collectionIds }),
    ...(enabled !== undefined && { enabled }),
    ...(qa_approved !== undefined && { qa_approved }),
    ...(event_type && event_type.length > 0 && { event_type }),
    ...(sortBy && { sortBy }),
    ...(sortOrder && { sortOrder }),
    ...(search && { search }),
    ...(permanent !== undefined && { permanent }),
    ...(checkBrokenRefs !== undefined && { checkBrokenRefs }),
    ...(page !== undefined && { page }),
    ...(pageSize !== undefined && { pageSize }),
  };

  return filter;
};
